import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { logError, logWarning } from '../utils/errorLogging';
import { authFetch } from '../utils/authFetch';
import { trackPastIncidentsFilterEmptyResult } from '../utils/modernUITracking';

/**
 * Transform a raw problem record into a flat display object.
 * Uses defensive optional chaining on every field.
 * Returns null if the problem has no outages.
 * `artifacts` is the response's per-incident map (US2135577); a missing map or entry means no link.
 */
const transformProblem = (p, artifacts = {}) => {
  const outageKeys = Object.keys(p?.outages || {});
  if (outageKeys.length === 0) return null;

  const firstOutage = p?.outages?.[outageKeys[0]];
  if (!firstOutage) return null;

  const brandParts = [];

  if (firstOutage?.affectedBrand) {
    const brandName = typeof firstOutage.affectedBrand === 'object'
      ? firstOutage.affectedBrand?.name
      : firstOutage.affectedBrand;
    if (brandName) brandParts.push(brandName);
  }

  if (firstOutage?.affectedBrandCapability) {
    const capabilities = Array.isArray(firstOutage.affectedBrandCapability)
      ? firstOutage.affectedBrandCapability
      : [firstOutage.affectedBrandCapability];

    capabilities.forEach(capability => {
      if (capability) {
        const capabilityName = typeof capability === 'object'
          ? capability?.name || capability?.label || capability?.value
          : capability;
        if (capabilityName) brandParts.push(capabilityName);
      }
    });
  }

  return {
    number: p?.number ?? '',
    priority: firstOutage?.priority,
    shortDescription: firstOutage?.short_description,
    status: firstOutage?.status,
    slackChannel: firstOutage?.slackChannel,
    incidentStart: firstOutage?.incidentStart,
    incidentEnd: firstOutage?.incidentEnd,
    affectedBrand: brandParts.join(', ') || '',
    execSummaryUrl: artifacts?.[p?.number]?.execSummaryUrl,
  };
};

/**
 * Only a 503 carries user-facing copy (the Exec Summary Document filter's `renderUserError`).
 * Other statuses come from `renderError`, whose body can hold internal error text, so they
 * keep the generic message.
 */
const errorFromResponse = async (response, fallback) => {
  if (response.status !== 503) return new Error(fallback);
  try {
    const body = await response.json();
    if (body && typeof body.error === 'string' && body.error) {
      const err = new Error(body.error);
      err.userFacing = true;
      return err;
    }
  } catch (e) {
    // fall through to the generic message
  }
  return new Error(fallback);
};

/**
 * Format a Date object as a YYYY-MM-DD string in UTC.
 * Returns '' for null/undefined/invalid Date.
 *
 * UTC accessors are used so the emitted date string matches the UTC anchor the
 * backend uses (`time.ParseInLocation("2006-01-02", ..., time.UTC)`). Using
 * local-time accessors would cause off-by-one-day shifts for users east/west of
 * UTC during the ms→date conversion at the storage layer.
 */
const formatDateForApi = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Custom hook for fetching and managing resolved (past) incident data.
 * US1937851: Browse Resolved Incidents with cursor-based pagination.
 * US1947404: Optional startDate/endDate range filter on incidentEnd.
 *
 * Features:
 * - Initial load with full loading state
 * - Cursor-based pagination via loadMore()
 * - No polling / no background refresh
 * - Preserves data on loadMore errors
 * - Atomic reset of problems, nextCursor, hasMore on filter change
 *
 * @param {Object} options
 * @param {boolean} [options.enabled=true]
 * @param {Date|null} [options.startDate=null]  Inclusive range start (YYYY-MM-DD sent to API)
 * @param {Date|null} [options.endDate=null]    Inclusive range end (YYYY-MM-DD sent to API)
 * @param {string[]} [options.priorities=[]]    Priority filter values (EP1-EP4) sent as repeated priority params
 * @param {string[]} [options.brands=[]]        Brand name filter values sent as repeated brand params
 * @param {boolean} [options.execSummaryDocument=false]  Only incidents with an Exec Summary Document link
 * @returns {Object} { problems, loading, loadingMore, error, hasMore, loadMore, handleRetry }
 */
const useHistoricalProblems = ({ enabled = true, startDate = null, endDate = null, priorities = [], brands = [], execSummaryDocument = false } = {}) => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState('');
  const [retryTrigger, setRetryTrigger] = useState(0);
  const loadingMoreRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const fetchingInitialRef = useRef(false);
  const problemCountRef = useRef(0);
  problemCountRef.current = problems.length;

  // US1947404 / US1984532: filterKey (a stable serialised identity that drives the
  // state-reset effect) and filterQuery (the API query suffix appended to every fetch)
  // are derived together from the SAME inputs in one memo, so they can never desync —
  // e.g. if a caller passes inline array literals. Both are primitive strings, so they
  // compare by value downstream even when the source array identities are unstable.
  // Either or both date bounds may be present — a single bound produces a GTE or LTE
  // filter at the backend; both produce a BETWEEN. Priority/brand are bare repeated
  // params (priority=EP1&priority=EP2) read via req.URL.Query()["priority"].
  const startDateParam = formatDateForApi(startDate);
  const endDateParam = formatDateForApi(endDate);
  const { filterKey, filterQuery } = useMemo(() => {
    const key = JSON.stringify({
      start: startDateParam,
      end: endDateParam,
      priorities: [...priorities].sort(),
      brands: [...brands].sort(),
      execSummaryDocument,
    });
    const parts = [];
    if (startDateParam) parts.push(`startDate=${encodeURIComponent(startDateParam)}`);
    if (endDateParam) parts.push(`endDate=${encodeURIComponent(endDateParam)}`);
    priorities.forEach(p => parts.push(`priority=${p}`));  // EP1-EP4 are safe ASCII, no encodeURIComponent needed
    brands.forEach(b => parts.push(`brand=${encodeURIComponent(b)}`));  // brand names may have spaces
    if (execSummaryDocument) parts.push('execSummaryDocument=true');
    const query = parts.length > 0 ? `&${parts.join('&')}` : '';
    return { filterKey: key, filterQuery: query };
  }, [startDateParam, endDateParam, priorities, brands, execSummaryDocument]);

  // A filtered request can run for seconds, so a response may land after the filter changed;
  // requests compare the key they started under against this before touching state.
  const filterKeyRef = useRef(filterKey);
  filterKeyRef.current = filterKey;

  // US1984532: a filter that matches nothing is only "empty" once the last page is
  // reached; a sparse filter can return empty pages while more remain.
  const trackEmptyResult = useCallback(() => {
    const hasDate = !!(startDateParam || endDateParam);
    if (priorities.length === 0 && brands.length === 0 && !hasDate && !execSummaryDocument) return;
    trackPastIncidentsFilterEmptyResult({
      priorityCount: priorities.length,
      brandCount: brands.length,
      hasDate,
      hasExecSummaryDocument: execSummaryDocument,
      dimensionCount: (hasDate ? 1 : 0) + (priorities.length > 0 ? 1 : 0) + (brands.length > 0 ? 1 : 0) + (execSummaryDocument ? 1 : 0),
    });
  // filterQuery recomputes whenever any of the filter inputs change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery]);

  /**
   * Fetch the first page of resolved problems on mount or filter change.
   */
  const fetchInitial = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    if (hasLoadedRef.current) return;

    const requestKey = filterKeyRef.current;
    const isStale = () => requestKey !== filterKeyRef.current;
    fetchingInitialRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/api/v1/problem?category=resolved&limit=20${filterQuery}`);

      if (!response.ok) {
        throw await errorFromResponse(response, 'Unable to fetch past incidents');
      }

      const result = await response.json();
      if (isStale()) return;
      const transformed = (result.problems || []).map((p) => transformProblem(p, result.artifacts)).filter(Boolean);

      setProblems(transformed);
      setHasMore(!!result.hasMore);
      setNextCursor(result.nextCursor || '');
      hasLoadedRef.current = true;

      if (transformed.length === 0 && !result.hasMore) trackEmptyResult();
    } catch (err) {
      if (isStale()) return;
      logError('useHistoricalProblems.fetchInitial', err, {});
      setError(err);
      setProblems([]);
    } finally {
      if (!isStale()) {
        fetchingInitialRef.current = false;
        setLoading(false);
      }
    }
  // priorities, brands, startDateParam, and endDateParam are intentionally omitted:
  // filterQuery already recomputes (and recreates fetchInitial) whenever any of them
  // change, so adding them here would be redundant and misleading.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterQuery, trackEmptyResult]);

  /**
   * Load the next page of results and append to the existing list.
   * Filters are scoped to the cursor; the same filterQuery is appended.
   */
  const loadMore = useCallback(async () => {
    if (!enabled || !nextCursor || !hasMore || loadingMoreRef.current || fetchingInitialRef.current) return;
    loadingMoreRef.current = true;
    const requestKey = filterKeyRef.current;
    const isStale = () => requestKey !== filterKeyRef.current;

    setLoadingMore(true);

    try {
      const response = await authFetch(
        `/api/v1/problem?category=resolved&limit=20&cursor=${encodeURIComponent(nextCursor)}${filterQuery}`
      );

      if (!response.ok) {
        throw await errorFromResponse(response, 'Unable to load more past incidents');
      }

      const result = await response.json();
      if (isStale()) return;
      const transformed = (result.problems || []).map((p) => transformProblem(p, result.artifacts)).filter(Boolean);

      if (problemCountRef.current === 0 && transformed.length === 0 && !result.hasMore) trackEmptyResult();
      setProblems(prev => [...prev, ...transformed]);
      setHasMore(!!result.hasMore);
      setNextCursor(result.nextCursor || '');
      setError(null);
    } catch (err) {
      if (isStale()) return;
      logError('useHistoricalProblems.loadMore', err, {});
      logWarning('useHistoricalProblems.loadMore', 'Failed to load more, preserving existing data');
      // Preserve existing problems on loadMore error but surface the error to the UI.
      // Temporarily suppress hasMore so the IntersectionObserver does not re-fire
      // the failing request on every scroll without back-off.
      setError(err);
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [enabled, nextCursor, hasMore, filterQuery, trackEmptyResult]);

  /**
   * Retry whichever request failed. With a cursor in hand the failure was a later page
   * (or the auto-advance past empty pages): re-enable hasMore and bump retryTrigger so
   * the retry useEffect fires loadMore after state updates are committed -- avoiding
   * stale closure issues. With no cursor the first page failed, so fetch it again.
   */
  // handleRetry sets hasMore=true before the retryTrigger effect fires, creating a brief
  // window where the IntersectionObserver can fire. The loadingMoreRef guard in loadMore
  // prevents the observer from executing a concurrent fetch.
  const handleRetry = useCallback(() => {
    if (nextCursor) {
      setHasMore(true);
      setError(null);
      setRetryTrigger(n => n + 1);
      return;
    }
    hasLoadedRef.current = false;
    fetchInitial();
  }, [nextCursor, fetchInitial]);

  // Retry effect: runs after handleRetry's state updates are committed,
  // so loadMore sees current hasMore and nextCursor values.
  // retryTrigger is reset to 0 immediately after firing so that the effect
  // does not re-run when loadMore is recreated after a successful fetch.
  useEffect(() => {
    if (retryTrigger > 0 && enabled) {
      setRetryTrigger(0);
      loadMore();
    }
  }, [retryTrigger, loadMore, enabled]);

  // US1947404: Atomic filter reset.
  // When filterKey changes, clear accumulated state synchronously and force
  // fetchInitial to re-run. The hasLoadedRef gate prevents fetchInitial from
  // re-firing in its own effect once a fetch has completed, so we must clear
  // it here. All resets occur BEFORE the fetchInitial useEffect re-fires.
  // setLoading(true) is set here so the UI shows a loading state immediately
  // instead of flashing an empty state between the reset and the next fetch.
  useEffect(() => {
    hasLoadedRef.current = false;
    fetchingInitialRef.current = false;
    setProblems([]);
    setNextCursor('');
    setHasMore(false);
    setError(null);
    setLoading(true);
    // fetchInitial useEffect below will then run because its dependency
    // (filterQuery via fetchInitial identity) changed.
  }, [filterKey]);

  // Initial fetch on mount (and after filter resets clear hasLoadedRef).
  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  // US1997496: The backend excludes temporary (PRBT) problems from resolved results, so a
  // page can arrive empty while more pages remain. With no rows rendered, the infinite-scroll
  // sentinel never mounts and nothing triggers loadMore — auto-advance until we have rows or
  // run out of pages. Each loadMore advances the cursor, so this terminates when hasMore is false.
  useEffect(() => {
    if (enabled && !loading && !loadingMore && hasMore && nextCursor && problems.length === 0) {
      loadMore();
    }
  }, [enabled, loading, loadingMore, hasMore, nextCursor, problems.length, loadMore]);

  return {
    problems,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    handleRetry,
  };
};

export default useHistoricalProblems;
